import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  completarEtapa,
  crearEtapa,
  editarEtapa,
  type EtapaImportacion,
} from '@/shared/api/controllers/importaciones';
import { Button } from '@/shared/components/ui/button';
import { DatePicker } from '@/shared/components/ui/date-picker';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog';
import { Input } from '@/shared/components/ui/input';
import { Label } from '@/shared/components/ui/label';
import { Switch } from '@/shared/components/ui/switch';
import { hoyAr } from '@/features/facturacion/plazoNc';

const AVISAR_POR_DEFECTO = '3';

/** Entero >= 0 de un campo de texto, o null. */
const entero = (v: string) => (/^\d+$/.test(v.trim()) ? Number(v.trim()) : null);

/** Alta y edición de una etapa de la importación. */
export function EtapaDialog({
  importacionId,
  etapas,
  etapa,
  open,
  onClose,
}: {
  importacionId: number;
  /** Las de la importación: para avisar qué etapa deja de ser el arribo. */
  etapas: EtapaImportacion[];
  /** null = alta. */
  etapa: EtapaImportacion | null;
  open: boolean;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [nombre, setNombre] = useState('');
  const [dias, setDias] = useState('');
  const [avisar, setAvisar] = useState(AVISAR_POR_DEFECTO);
  const [esArribo, setEsArribo] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setNombre(etapa?.nombre ?? '');
    setDias(etapa ? String(etapa.dias) : '');
    setAvisar(etapa ? String(etapa.avisarDiasAntes) : AVISAR_POR_DEFECTO);
    setEsArribo(etapa?.esArribo ?? false);
    setError(null);
  }, [open, etapa]);

  const diasN = entero(dias);
  const avisarN = entero(avisar);
  const valido = nombre.trim() !== '' && diasN != null && avisarN != null;
  const otroArribo = esArribo ? etapas.find((e) => e.esArribo && e.id !== etapa?.id) : undefined;

  const mutation = useMutation({
    mutationFn: async () => {
      const body = { nombre: nombre.trim(), dias: diasN!, avisarDiasAntes: avisarN!, esArribo };
      if (etapa) await editarEtapa(importacionId, etapa.id, body);
      else await crearEtapa(importacionId, body);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['importaciones'] });
      onClose();
    },
    onError: (e) => setError(e instanceof Error ? e.message : 'No se pudo guardar la etapa.'),
  });

  return (
    <Dialog open={open} onOpenChange={(o) => !o && !mutation.isPending && onClose()}>
      <DialogContent className="max-w-md" processing={mutation.isPending && 'Guardando…'}>
        <DialogHeader>
          <DialogTitle>{etapa ? 'Editar etapa' : 'Nueva etapa'}</DialogTitle>
          <DialogDescription>
            Las fechas de las etapas siguientes se corren solas según los días de esta.
          </DialogDescription>
        </DialogHeader>
        <DialogBody className="space-y-3">
          <div className="space-y-1">
            <Label htmlFor="etapa-nombre">Nombre</Label>
            <Input
              id="etapa-nombre"
              variant="filled"
              placeholder="Fabricación"
              autoFocus
              value={nombre}
              onChange={(e) => setNombre(e.target.value)}
            />
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1">
              <Label htmlFor="etapa-dias">Días</Label>
              <Input
                id="etapa-dias"
                variant="filled"
                inputMode="numeric"
                placeholder="45"
                value={dias}
                onChange={(e) => setDias(e.target.value)}
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="etapa-avisar">Avisar N días antes</Label>
              <Input
                id="etapa-avisar"
                variant="filled"
                inputMode="numeric"
                value={avisar}
                onChange={(e) => setAvisar(e.target.value)}
              />
            </div>
          </div>
          <div className="space-y-1">
            <div className="flex items-center justify-between rounded-lg border px-3 py-2">
              <Label htmlFor="etapa-arribo">Es el arribo</Label>
              <Switch id="etapa-arribo" checked={esArribo} onCheckedChange={setEsArribo} />
            </div>
            <p className="text-xs text-muted-foreground">
              {otroArribo
                ? `Reemplaza a "${otroArribo.nombre}" como arribo: el fin de esta etapa define la fecha probable de llegada.`
                : 'El fin de esta etapa define la fecha probable de llegada. Sólo una etapa puede ser el arribo.'}
            </p>
          </div>
          {error && <p className="text-sm font-medium text-destructive">{error}</p>}
        </DialogBody>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={mutation.isPending}>
            Cancelar
          </Button>
          <Button onClick={() => mutation.mutate()} disabled={!valido || mutation.isPending}>
            {etapa ? 'Guardar cambios' : 'Agregar etapa'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Completar una etapa con su fecha real (hoy por defecto) o corregir la de una ya completada. */
export function CompletarEtapaDialog({
  importacionId,
  etapa,
  onClose,
}: {
  importacionId: number;
  etapa: EtapaImportacion | null;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [fecha, setFecha] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!etapa) return;
    setFecha(etapa.fechaReal ?? hoyAr());
    setError(null);
  }, [etapa]);

  const mutation = useMutation({
    mutationFn: () => completarEtapa(importacionId, etapa!.id, fecha),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['importaciones'] });
      onClose();
    },
    onError: (e) => setError(e instanceof Error ? e.message : 'No se pudo completar la etapa.'),
  });

  return (
    <Dialog open={etapa != null} onOpenChange={(o) => !o && !mutation.isPending && onClose()}>
      <DialogContent className="max-w-sm" processing={mutation.isPending && 'Guardando…'}>
        <DialogHeader>
          <DialogTitle>{etapa?.fechaReal ? 'Fecha real de la etapa' : 'Completar etapa'}</DialogTitle>
          <DialogDescription>
            {etapa?.nombre}. Las etapas siguientes arrancan desde esta fecha.
          </DialogDescription>
        </DialogHeader>
        <DialogBody className="space-y-2">
          <Label>Fecha real</Label>
          <DatePicker value={fecha} onChange={setFecha} className="w-full" ariaLabel="Fecha real" clearable={false} />
          {error && <p className="text-sm font-medium text-destructive">{error}</p>}
        </DialogBody>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={mutation.isPending}>
            Cancelar
          </Button>
          <Button onClick={() => mutation.mutate()} disabled={fecha === '' || mutation.isPending}>
            {etapa?.fechaReal ? 'Guardar fecha' : 'Completar'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
